import { Component, signal, inject, OnInit, computed, OnDestroy, HostListener, effect, untracked, ViewChild, ElementRef, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';

import { CommonModule } from '@angular/common';
import { IconComponentV2 } from '../../../shared/components/icon-component/icon.component';
import { MarkdownModule, provideMarkdown } from 'ngx-markdown';
import { PaginatedTableComponent, TableHeader, TableActionOptions } from '../../../shared/components/tables/paginated-table/paginated-table.component';
import { ApiService } from '../../../core/services/api.service';
import { CallAuditAudioService } from '../../../core/services/call-audit-audio.service';
import { apiUrlFor } from '../../../core/services/prism-new-routing';
import { CommonService } from '../../../core/services/common.service';
import { AuthService } from '../../../core/services/auth.service';
import { ProductConfigService } from '../../../core/services/product-config.service';
import { DropdownComponent, DropdownOption } from '../../../shared/components/ui/dropdown/dropdown.component';
import { AiChatPanelComponent } from '../../public/chatbot/ai-chat-panel/ai-chat-panel.component';
import { ChatHistorySidebarComponent } from '../../public/chatbot/chat-history-sidebar/chat-history-sidebar.component';
import jsPDF from 'jspdf';
import { SlidePanelComponent } from '../../../shared/components/slide-panel/slide-panel.component';
import { IconComponent } from '../../../shared/icons/icon.component';
import { ToggleComponent } from '../../../shared/components/ui/toggle/toggle.component';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationModalComponent, ConfirmationModalData } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { ToasterService } from '../../../core/services/toaster.service';
import { CallAuditActivityPanelComponent } from './components/call-audit-activity-panel/call-audit-activity-panel.component';
import { AuditActivityTracker } from './services/audit-activity-tracker';
import { ChatSocketService } from '../../../core/services/chatSocketService';

@Component({
  selector: 'app-call-quality',
  standalone: true,
  imports: [CommonModule, FormsModule, IconComponentV2, IconComponent, PaginatedTableComponent, DropdownComponent, MarkdownModule, AiChatPanelComponent, ChatHistorySidebarComponent, SlidePanelComponent, ToggleComponent, CallAuditActivityPanelComponent],

  providers: [provideMarkdown(), AuditActivityTracker],
  templateUrl: './call-quality.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    :host ::ng-deep table td:nth-last-child(1),
    :host ::ng-deep table th:nth-last-child(1) {
      width: 230px !important;
      min-width: 230px !important;
      max-width: 230px !important;
    }
    :host ::ng-deep table td:nth-last-child(2),
    :host ::ng-deep table th:nth-last-child(2) {
      width: 250px !important;
      min-width: 250px !important;
      max-width: 250px !important;
    }

    ::ng-deep .sidebar-panel-container {
      top: 64px !important;
      height: calc(100vh - 64px) !important;
      z-index: 1040 !important;
    }

    ::ng-deep .full-screen-panel-container {
      position: fixed !important;
      top: var(--navbar-height, 64px) !important;
      left: var(--sidebar-width, 0px) !important;
      width: calc(100vw - var(--sidebar-width, 0px)) !important;
      max-width: calc(100vw - var(--sidebar-width, 0px)) !important;
      z-index: 1040 !important;
      height: calc(100vh - var(--navbar-height, 64px)) !important;
    }

    ::ng-deep .markdown-content {
      p { margin-bottom: 0.5em; line-height: 1.5; }
      p:last-child { margin-bottom: 0; }
      strong { font-weight: 700; color: inherit; }
      em { font-style: italic; }
      ul { list-style: disc; margin: 0.5em 0; padding-left: 1.5em; }
      ol { list-style: decimal; margin: 0.5em 0; padding-left: 1.5em; }
    }
  `]
})
export class CallQualityComponent implements OnInit, OnDestroy {
  private _apiService = inject(ApiService);
  public _commonService = inject(CommonService);
  public authService = inject(AuthService);
  private _productConfigService = inject(ProductConfigService);
  private dialog = inject(MatDialog);
  private toasterService = inject(ToasterService);
  private auditTracker = inject(AuditActivityTracker);
  private callAudio = inject(CallAuditAudioService);
  private chatSocketService = inject(ChatSocketService);

  selectedProductId = signal<string | null>(null);
  isTest = signal<boolean>(false);

  isDenefits = computed(() =>
    this._productConfigService.isDenefitsProduct(this.selectedProductId())
  );


  isChatOpen = signal(false);
  hasStartedChat = signal(false);
  userInputHub = signal('');
  chatLayoutMode = signal<'floating' | 'fullscreen'>('floating');
  isChatHistoryOpen = signal(false);

  @ViewChild(AiChatPanelComponent) aiChatPanel?: AiChatPanelComponent;
  @ViewChild(ChatHistorySidebarComponent) chatHistorySidebar?: ChatHistorySidebarComponent;
  @ViewChild(CallAuditActivityPanelComponent) activityPanel?: CallAuditActivityPanelComponent;

  toggleChat() {
    const nextState = !this.isChatOpen();
    this.isChatOpen.set(nextState);

    if (nextState) {
      // Trigger history fetch when chatbot is opened, but keep sidebar closed
      setTimeout(() => {
        this.chatHistorySidebar?.fetchChatSessions();
      }, 4000);
    }
  }

  closeChat() {
    this.isChatOpen.set(false);
    this.hasStartedChat.set(false);
    this.chatLayoutMode.set('floating');

    // Call the session close API on the chat panel
    if (this.aiChatPanel) {
      this.aiChatPanel.closeChatSession();
    }
  }

  startChat(suggestion?: string) {
    const query = suggestion || this.userInputHub();
    if (!query) return;
    this.hasStartedChat.set(true);
    setTimeout(() => {
      this.aiChatPanel?.sendQuery(query);
      this.userInputHub.set('');
    }, 0);
  }

  toggleLayoutMode() {
    const nextMode = this.chatLayoutMode() === 'floating' ? 'fullscreen' : 'floating';
    this.chatLayoutMode.set(nextMode);
  }

  setLayoutMode(mode: 'floating' | 'fullscreen') {
    this.chatLayoutMode.set(mode);
  }

  toggleChatHistory() {
    this.isChatHistoryOpen.set(!this.isChatHistoryOpen());
  }

  onSessionSelected(session: any) {
    if (window.innerWidth < 640 || this.chatLayoutMode() === 'floating') {
      this.isChatHistoryOpen.set(false);
    }
    this.isChatOpen.set(true);
    this.hasStartedChat.set(true);

    // Call loadChatSession. Since we're using [class.hidden], 
    // aiChatPanel is already rendered and available.
    if (this.aiChatPanel) {
      this.aiChatPanel.loadChatSession(session);
    } else {
      // Fallback for extreme cases (though [class.hidden] should prevent this)
      setTimeout(() => {
        this.aiChatPanel?.loadChatSession(session);
      }, 0);
    }
  }

  activePlayingCall = signal<any | null>(null);
  durationSub: Subscription | null = null;
  private streamSub?: Subscription;
  reportsSub: Subscription | null = null;
  filterOptionsSub: Subscription | null = null;
  private _isChangingSource = false;
  activeAuditCall = signal<any | null>(null);
  activeTranscriptCall = signal<any>(null);
  showAuditDetail = signal<boolean>(false);
  showTranscriptionInAudit = signal<boolean>(false);

  // --- Session Tracking ---
  activeSession = signal<any | null>(null);
  hasSessionAccess = signal<boolean>(false);
  isStartingSession = signal<boolean>(false);
  isFetchingSession = signal<boolean>(false);
  isTimerRunning = signal<boolean>(false);
  elapsedTime = signal<string>('00:00');
  otherAgentElapsedTime = signal<string>('00:00');
  sessionId: string = crypto.randomUUID();
  timerInterval: any;
  otherAgentTimerInterval: any;
  sessionPollingInterval: any;
  private sessionClosedForAudit: string | null = null; // tracks auditId for which close was already sent
  private fetchSessionSub: Subscription | null = null;
  // ------------------------

  currentTranscriptIndex = computed(() => {
    const call = this.activePlayingCall();
    if (!call || !call.formattedTranscript) return -1;
    const time = this.currentTime();
    const transcript = call.formattedTranscript;
    let index = -1;
    for (let i = 0; i < transcript.length; i++) {
      if (transcript[i].timeSeconds <= time) {
        index = i;
      } else {
        break;
      }
    }
    return index;
  });

  currentPage = signal<number>(1);
  pageSize = signal<number>(10);
  totalItems = signal<number>(0);
  isLoading = signal<boolean>(false);
  isFiltersLoading = signal<boolean>(false);
  onNewChatRequested() {
    this.hasStartedChat.set(false);
    this.isChatHistoryOpen.set(false);
    if (this.aiChatPanel) {
      this.aiChatPanel.resetChat();
    }
  }

  isFilterSidebarOpen = signal<boolean>(false);

  isFeedbackModalOpen = signal<boolean>(false);
  feedbackObservations = signal<string>('');
  feedbackRecommendations = signal<string>('');
  feedbackComments = signal<string>('');

  openFeedbackModal() {
    this.isFeedbackModalOpen.set(true);
    this.feedbackObservations.set('');
    this.feedbackRecommendations.set('');
    this.feedbackComments.set('');
  }

  closeFeedbackModal() {
    this.isFeedbackModalOpen.set(false);
  }

  submitFeedback() {
    if (this.feedbackObservations().trim().length < 30 || this.feedbackRecommendations().trim().length < 30 || (this.feedbackComments().trim().length > 0 && this.feedbackComments().trim().length < 30)) return;

    let productPid: any = 7;
    const productId = this.selectedProductId();
    const products = this._apiService.products();

    if (productId) {
      const selectedProduct = products.find((p: any) => p.id === productId);
      if (selectedProduct && selectedProduct.pid !== undefined) {
        productPid = selectedProduct.pid;
      } else {
        try {
          const stored = sessionStorage.getItem('dynamic_routes');
          if (stored) {
            const dynamicRoutes = JSON.parse(stored);
            const matched = dynamicRoutes.find((p: any) => p.product?.id === productId);
            if (matched && matched.product?.pid !== undefined) {
              productPid = matched.product.pid;
            }
          }
        } catch (e) { }
      }
    }

    const payload = {
      auditId: this.activeAuditCall()?.id?.toString() || '1',
      comments: this.feedbackComments().trim(),
      observations: this.feedbackObservations().trim(),
      recommendations: this.feedbackRecommendations().trim(),
      productPid: productPid
    };

    this._apiService.post('/call-audit/feedback', payload, { showLoader: true }).subscribe({
      next: () => {
        this.toasterService.success('Feedback submitted successfully!');
        this.closeFeedbackModal();
      },
      error: (err: any) => {
        const errorMessage = err?.error?.message || err?.message || 'Failed to submit feedback. Please try again.';
        this.toasterService.error(errorMessage);
      }
    });
  }

  calls = signal<any[]>([]);
  agentOptions = signal<DropdownOption[]>([]);
  teamOptions = signal<DropdownOption[]>([]);
  selectedAgent = signal<string | null>(null);
  selectedTeam = signal<string | null>(null);
  selectedRating = signal<string | null>(null);
  selectedCallType = signal<string | null>(null);
  selectedPlatform = signal<string | null>(null);

  ratingOptions = signal<DropdownOption[]>([
    { value: 'all', label: 'All Ratings' },
    { value: '5', label: '⭐⭐⭐⭐⭐' },
    { value: '4', label: '⭐⭐⭐⭐' },
    { value: '3', label: '⭐⭐⭐' },
    { value: '2', label: '⭐⭐' },
    { value: '1', label: '⭐' },
  ]);

  callTypeOptions = signal<DropdownOption[]>([
    { value: 'L1', label: 'L1' },
    { value: 'L2', label: 'L2' },
    { value: 'L3', label: 'L3' },
  ]);

  platformOptions = signal<DropdownOption[]>([
    { value: 'GoogleMeet', label: 'Google Meet' },
    { value: 'RingCentral', label: 'Ring Central' }
  ]);

  isPlaying = signal<boolean>(false);
  currentTime = signal<number>(0);
  audioDuration = signal<number>(0);
  private audio = new Audio();

  auditCategories = signal<any[]>([]);

  tableHeaders: TableHeader[] = [
    { label: 'Business Details', key: 'business', sortable: false },
    { label: 'Call Type', key: 'callType', sortable: false },
    { label: 'Agent/Team', key: 'agent', sortable: false },
    { label: 'Call Rating', key: 'rating', sortable: false },
    { label: 'Recording', key: 'recording', sortable: false },
    { label: 'Audit Report', key: 'audit', sortable: false, align: 'text-right' }
  ];
  tableActionOptions: TableActionOptions = {
    showToggleButton: false,
    columnLabel: 'Audit Report'
  };

  @ViewChild('playerTranscriptContainer') playerTranscriptContainer?: ElementRef;
  @ViewChild('auditTranscriptContainer') auditTranscriptContainer?: ElementRef;
  @ViewChild('sidebarTranscriptContainer') sidebarTranscriptContainer?: ElementRef;

  private toTitleCase(str: string): string {
    if (!str) return '';
    return str.toLowerCase().split(' ').map(word => {
      const w = word.trim();
      return w.charAt(0).toUpperCase() + w.slice(1);
    }).join(' ');
  }

  constructor() {
    this.checkSessionAccess();
    effect(() => {
      const productId = this._apiService.selectedProductId();
      if (productId) {
        untracked(() => {
          this.selectedProductId.set(productId);
          this.fetchFilterOptions();
          this.fetchData();
        });
      }
    });

    effect(() => {
      const index = this.currentTranscriptIndex();
      if (index === -1) return;

      untracked(() => {
        // Scroll in Player Card
        if (this.activePlayingCall() && !this.showAuditDetail()) {
          this.scrollToRow(this.playerTranscriptContainer, 'player-row-' + index);
        }

        // Scroll in Audit Detail
        if (this.showAuditDetail() && this.showTranscriptionInAudit()) {
          this.scrollToRow(this.auditTranscriptContainer, 'audit-row-' + index);
        }

        // Scroll in Sidebar
        if (this.activeTranscriptCall()) {
          this.scrollToRow(this.sidebarTranscriptContainer, 'sidebar-row-' + index);
        }
      });
    });
  }

  private scrollToRow(container: ElementRef | undefined, rowId: string) {
    if (!container) return;
    const row = container.nativeElement.querySelector('#' + rowId);
    if (row) {
      row.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  private getSelectedProduct(): string | null {
    const productId = this._apiService.getSelectedProductId() || sessionStorage.getItem('selected_product_id');
    if (!productId) return null;

    const stored = sessionStorage.getItem('dynamic_routes');
    const dynamicRoutes = stored ? JSON.parse(stored) : [];
    const selectedProduct = dynamicRoutes.find((p: any) => p.product?.id === productId);
    return selectedProduct?.product?.name || '';
  }

  ngOnInit() {
    this.fetchFilterOptions();
    this.setupAudioListeners();
    this.setupSocketListeners();
  }

  ngOnDestroy() {
    this.stopOtherAgentTimer();
    this.tearDownSocketListeners();

    const call = this.activeAuditCall();
    if (call) {
      this.chatSocketService.leaveAuditRoom(call.id);
    }

    if (this.reportsSub) {
      this.reportsSub.unsubscribe();
    }
    if (this.filterOptionsSub) {
      this.filterOptionsSub.unsubscribe();
    }
    this.streamSub?.unsubscribe();
    if (this.durationSub) {
      this.durationSub.unsubscribe();
    }

    // Ensure the session is closed if the user navigates away via Angular router
    // Skip if closeAuditDetail already sent the close request for this audit.
    if (call && this.isTimerRunning() && this.sessionClosedForAudit !== String(call.id)) {
      this.sessionClosedForAudit = String(call.id);
      const token = sessionStorage.getItem('access_token');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      fetch(apiUrlFor('POST', 'call-audit/session/close'), {
        method: 'POST',
        keepalive: true,
        headers,
        body: JSON.stringify({ auditId: call.id, sessionId: this.sessionId }),
        credentials: 'include'
      }).catch(err => console.error('Failed to close session on destroy', err));
    }
  }

  setupAudioListeners() {
    this.audio.ontimeupdate = () => {
      if (this._isChangingSource) return;
      // Add baseOffset to support server-side seeking UI
      this.currentTime.set(this.audio.currentTime + (this.activePlayingCall()?.baseOffset || 0));
    };
    this.audio.onloadedmetadata = () => {
      const browserDuration = this.audio.duration;
      const baseOffset = this.activePlayingCall()?.baseOffset || 0;

      if (isFinite(browserDuration) && browserDuration > 0) {
        // If we are at the beginning (no seek), the browser's duration is the absolute truth
        if (baseOffset === 0) {
          this.audioDuration.set(browserDuration);
          this.activePlayingCall.update(c => c ? ({ ...c, duration: browserDuration }) : null);
          if (this.activeAuditCall()?.id === this.activePlayingCall()?.id) {
            this.activeAuditCall.update(c => c ? ({ ...c, duration: browserDuration }) : null);
          }
        } else {
          // If we seeked, browserDuration is only the length of the remaining part.
          const totalProbed = browserDuration + baseOffset;
          // Only update if it's significantly different to avoid flickering small changes
          if (Math.abs(totalProbed - (this.audioDuration() || 0)) > 2) {
            this.audioDuration.set(totalProbed);
          }
        }
      }
    };
    this.audio.onended = () => {
      this.isPlaying.set(false);
    };
    this.audio.onplay = () => {
      this.isPlaying.set(true);
    };
    this.audio.onpause = () => {
      this.isPlaying.set(false);
    };
    this.audio.onerror = () => {
      console.error('Audio playback error:', this.audio.error);
      this.isPlaying.set(false);
    };
  }

  fetchFilterOptions() {
    if (this.filterOptionsSub) {
      this.filterOptionsSub.unsubscribe();
    }
    this.isFiltersLoading.set(true);
    const params: any = { page: 1, limit: 100 };
    const productName = this.getSelectedProduct();
    if (productName) params.productName = productName;
    params.isTest = this.isTest() ? true : false;

    this.filterOptionsSub = this._apiService.get('/call-audit/filter-options', params).subscribe({
      next: (res: any) => {
        if (res) {
          if (res.agents) {
            const agentOptions: string[] = res.agents.map((a: any) => {
              const name = (a.name || a || '').toString();
              return this.toTitleCase(name);
            });
            const uniqueAgents: DropdownOption[] = Array.from(new Set(agentOptions))
              .sort()
              .map((name: string) => ({ value: name, label: name }));
            this.agentOptions.set(uniqueAgents);
          }
          if (res.teams) {
            const teamOptions: string[] = res.teams.map((t: any) => {
              const name = (t.name || t || '').toString().replace(/_/g, ' ');
              return this.toTitleCase(name);
            });
            const uniqueTeams: DropdownOption[] = Array.from(new Set(teamOptions))
              .sort()
              .map((name: string) => ({ value: name, label: name }));
            this.teamOptions.set(uniqueTeams);
          }
        }
        this.isFiltersLoading.set(false);
      },
      error: (err) => {
        console.error('Error fetching filter options:', err);
        this.isFiltersLoading.set(false);
      }
    });
  }

  fetchData() {
    if (this.reportsSub) {
      this.reportsSub.unsubscribe();
    }
    this.isLoading.set(true);
    const params: any = {
      page: this.currentPage(),
      limit: this.pageSize(),
    };

    if (this.selectedAgent()) params.agentName = this.selectedAgent();
    if (this.selectedTeam()) params.teamName = this.selectedTeam();
    if (this.selectedRating()) params.star = this.selectedRating();
    if (this.selectedCallType()) params.callType = this.selectedCallType();
    if (this.selectedPlatform()) params.platform = this.selectedPlatform();

    const productName = this.getSelectedProduct();
    if (productName) {
      params.productName = productName;
    }
    params.isTest = this.isTest() ? true : false;

    this.reportsSub = this._apiService.get('/call-audit/reports', params).subscribe({
      next: (res: any) => {
        try {
          // Extremely robust data extraction to handle any level of wrapping
          let results: any[] = [];
          let pagination: any = null;

          if (res) {
            if (Array.isArray(res.results)) {
              results = res.results;
              pagination = res.pagination;
            } else if (res.data) {
              if (Array.isArray(res.data.results)) {
                results = res.data.results;
                pagination = res.data.pagination;
              } else if (res.data.data && Array.isArray(res.data.data.results)) {
                results = res.data.data.results;
                pagination = res.data.data.pagination;
              }
            }
          }

          if (results && results.length > 0) {
            const mappedCalls = results
              .filter(item => !!item) // Skip null/undefined items
              .map((item: any) => {
                try {
                  return {
                    ...item,
                    business: {
                      name: item.customerName || '-',
                      doctor: item.productName || '-',
                    },
                    callType: {
                      type: item.callType || 'Outbound',
                      platform: item.platform || 'Phone Call',
                      dateTime: item.recordingStartTime || item.createdAt
                    },
                    agent: {
                      name: item.agentName || '-',
                      team: item.teamName || '-'
                    },
                    rating: {
                      score: `${item.score ?? 0}/100`,
                      stars: Math.ceil((Number(item.score) || 0) / 20)
                    },
                    recording: {
                      progress: 0
                    },
                    audioUrl: item.audioUrl || (typeof item.referer === 'string' && item.referer.includes('http') ? item.referer : ''),
                    productName: item.productName || 'credee',
                    referer: item.referer || '',
                    duration: item.duration || this.estimateDuration(item),
                    isVerified: !!item.verifiedByName,
                    verifiedByName: item.verifiedByName ?? null,
                    verifiedAt: item.verifiedAt ?? null,
                    formattedTranscript: this.normalizeTranscript(item),
                    transcript: item.transcript
                  };
                } catch (e) {
                  console.error('Error mapping individual call record:', e, item);
                  return null;
                }
              })
              .filter(item => !!item); // Remove items that failed mapping

            this.calls.set(mappedCalls);
            this.totalItems.set(
              pagination?.totalRecords ||
              pagination?.total_results ||
              pagination?.total ||
              res?.total ||
              results.length || 0
            );
          } else {
            this.calls.set([]);
            this.totalItems.set(0);
          }
        } catch (err) {
          console.error('Error processing call audit reports:', err);
          this.calls.set([]);
        } finally {
          this.isLoading.set(false);
        }
      },
      error: (err) => {
        console.error('Error fetching call audit reports:', err);
        this.isLoading.set(false);
      }
    });
  }

  onPageChange(page: number) {
    if (this.isLoading()) return;
    this.currentPage.set(page);
    this.fetchData();
  }

  onPageSizeChange(size: number) {
    if (this.isLoading()) return;
    this.pageSize.set(size);
    this.currentPage.set(1);
    this.fetchData();
  }

  onDummyToggleChange(val: boolean) {
    if (this.isLoading()) return;
    this.isTest.set(val);
    this.currentPage.set(1);
    this.fetchData();
  }

  onAgentChange(agent: any) {
    if (this.isLoading()) return;
    this.selectedAgent.set(agent === 'All Agents' ? null : agent);
    this.currentPage.set(1);
    this.fetchData();
  }

  onTeamChange(team: any) {
    if (this.isLoading()) return;
    this.selectedTeam.set(team === 'All Teams' ? null : team);
    this.currentPage.set(1);
    this.fetchData();
  }

  onRatingChange(rating: any) {
    if (this.isLoading()) return;
    this.selectedRating.set(rating === 'all' ? null : rating);
    this.currentPage.set(1);
    this.fetchData();
  }

  onCallTypeChange(callType: any) {
    if (this.isLoading()) return;
    this.selectedCallType.set(callType);
    this.currentPage.set(1);
    this.fetchData();
  }

  onPlatformChange(platform: any) {
    if (this.isLoading()) return;
    this.selectedPlatform.set(platform);
    this.currentPage.set(1);
    this.fetchData();
  }

  hasActiveFilters(): boolean {
    return !!this.selectedAgent() || !!this.selectedTeam() || !!this.selectedRating() || !!this.selectedCallType() || !!this.selectedPlatform() || this.isTest();
  }

  resetFilters() {
    if (this.isLoading()) return;
    this.isTest.set(false);
    this.selectedAgent.set(null);
    this.selectedTeam.set(null);
    this.selectedRating.set(null);
    this.selectedCallType.set(null);
    this.selectedPlatform.set(null);
    this.currentPage.set(1);
    this.fetchFilterOptions();
    this.fetchData();
  }

  private isSegmentedTranscript(entries: any[]): boolean {
    if (!Array.isArray(entries) || entries.length === 0) return false;
    const first = entries[0];
    return first != null &&
      typeof first === 'object' &&
      typeof first.start === 'number' &&
      typeof first.transcript === 'string' &&
      first.timestamp === undefined &&
      first.time_stamp === undefined &&
      first.text === undefined &&
      first.content === undefined;
  }

  private getSegmentedTranscriptEntries(transcript: any): any[] | null {
    const candidates = [
      transcript,
      transcript?.utterances,
      transcript?.segments,
    ];
    for (const candidate of candidates) {
      if (this.isSegmentedTranscript(candidate)) return candidate;
    }
    return null;
  }

  private normalizeTranscript(item: any): any[] {
    const agentName = item.agentName || item.agent?.name || '';
    const customerName = item.customerName || item.customer?.name || '';

    const getTimestampInSeconds = (ts: any): number => {
      if (ts == null) return 0;
      if (typeof ts === 'string' && isNaN(Number(ts))) {
        const parsed = new Date(ts).getTime();
        return isNaN(parsed) ? 0 : parsed / 1000;
      }
      const num = Number(ts);
      if (num > 10000000000) {
        return num / 1000;
      }
      return num;
    };

    let transcript = item.transcript;
    if (typeof transcript === 'string') {
      try { transcript = JSON.parse(transcript); } catch { }
    }

    // Case 1: googleTranscript (New/Streaming) or Array of message objects
    let googleTranscript = (Array.isArray(item.googleTranscript) && item.googleTranscript.length > 0) ? item.googleTranscript : null;
    if (!googleTranscript) {
      googleTranscript = (Array.isArray(transcript?.googleTranscript) && transcript.googleTranscript.length > 0) ? transcript.googleTranscript : null;
    }
    if (!googleTranscript && Array.isArray(transcript) && transcript.length > 0 && !this.isSegmentedTranscript(transcript)) {
      googleTranscript = transcript;
    }
    if (googleTranscript) {
      // Use recordingStartTime if available, otherwise fallback to first transcript timestamp
      const recordingStartTime = item.recordingStartTime;
      const baseTsSec = recordingStartTime
        ? getTimestampInSeconds(recordingStartTime)
        : getTimestampInSeconds(googleTranscript[0].timestamp || googleTranscript[0].time_stamp || 0);

      return googleTranscript.map((msg: any) => {
        const rawTs = getTimestampInSeconds(msg.timestamp || msg.time_stamp || 0) || baseTsSec;
        const relativeSeconds = Math.max(0, rawTs - baseTsSec);

        const speaker = (msg.speaker || '').toString();
        const role = (msg.role || '').toString();
        const speakerLower = speaker.toLowerCase();
        const roleLower = role.toLowerCase();

        const isAgent = ['advisor', 'agent', 'salesagent'].includes(speakerLower) ||
          ['advisor', 'agent', 'salesagent'].includes(roleLower) ||
          (agentName && speakerLower.includes(agentName.toLowerCase()));
        const isAI = roleLower === 'llm' || speakerLower === 'ai' || roleLower === 'ai';

        const msgSpeaker = Array.isArray(msg.speaker) ? (msg.speaker[0] || '') : (msg.speaker || '');
        const speakerLabel = isAI ? 'AI' : (msgSpeaker || (isAgent ? (agentName || 'Agent') : (customerName || 'User')));

        return {
          timeSeconds: relativeSeconds,
          time: this.formatTime(relativeSeconds),
          speaker: speakerLabel,
          role: isAI ? 'AI' : (isAgent ? 'AGENT' : 'CUSTOMER'),
          text: msg.text || msg.content || ''
        };
      });
    }

    // Case 2: normalized_transcript (Legacy/Phone)
    if (transcript?.normalized_transcript && Array.isArray(transcript.normalized_transcript)) {
      return transcript.normalized_transcript.map((line: any) => {
        if (typeof line !== 'string') return { time: '', timeSeconds: 0, speaker: 'Unknown', text: String(line || '') };
        const match = line.match(/^\[(\d{2}:\d{2})\]\s+([^:]+):\s+(.*)$/);
        if (match) {
          const rawSpeaker = match[2] || '';
          const speakerLower = rawSpeaker.toLowerCase();
          const isAI = rawSpeaker === 'AI' || rawSpeaker === 'llm';
          const isAgent = ['advisor', 'agent', 'salesagent'].includes(speakerLower) || (agentName && speakerLower.includes(agentName.toLowerCase()));

          return {
            time: match[1],
            timeSeconds: this.parseTimeToSeconds(match[1]),
            speaker: isAI ? 'AI' : (isAgent ? (agentName || 'Agent') : (customerName || 'User')),
            role: isAI ? 'AI' : (isAgent ? 'AGENT' : 'CUSTOMER'),
            text: match[3]
          };
        }
        return { time: '', timeSeconds: 0, speaker: 'Unknown', role: 'SYSTEM', text: line };
      });
    }

    // Case 3: Segmented transcript (start/end, numeric speaker — e.g. Deepgram)
    const segmentedTranscript = this.getSegmentedTranscriptEntries(transcript);
    if (segmentedTranscript) {
      const agentSpeakerId = item.agentSpeakerId ?? transcript?.agentSpeakerId ?? 0;
      return segmentedTranscript.map((seg: any) => {
        const timeSeconds = Math.max(0, seg.start ?? 0);
        const speakerId = typeof seg.speaker === 'number' ? seg.speaker : Number(seg.speaker);
        const isAgent = !Number.isNaN(speakerId) && speakerId === agentSpeakerId;

        return {
          timeSeconds,
          time: this.formatTime(timeSeconds),
          speaker: isAgent ? (agentName || 'Agent') : (customerName || 'User'),
          role: isAgent ? 'AGENT' : 'CUSTOMER',
          text: seg.transcript || seg.text || ''
        };
      });
    }

    return [];
  }

  parseTimeToSeconds(time: string): number {
    if (!time) return 0;
    const parts = time.split(':');
    if (parts.length === 2) {
      return parseInt(parts[0]) * 60 + parseInt(parts[1]);
    } else if (parts.length === 3) {
      return parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
    }
    return 0;
  }

  playCall(call: any, startTime: number = 0, force: boolean = false) {
    if (!force && this.activePlayingCall()?.id === call.id && startTime === 0) {
      this.togglePlay();
      return;
    }

    // A fresh play from the start (seeks pass startTime > 0 and are tracked separately).
    if (startTime === 0) {
      this.auditTracker.track('AUDIO_PLAYED');
    }

    // Reset currentTime to show starting point
    this.currentTime.set(startTime);

    // Store the start time so the UI can calculate the correct position
    const baseOffset = Math.floor(startTime);
    this.activePlayingCall.update(c => c ? ({ ...c, baseOffset }) : ({ ...call, baseOffset }));

    // Cancel any previous pending duration request
    this.durationSub?.unsubscribe();

    if (!this.callAudio.canPlay(call)) return;

    // Use pre-cached duration from database if available
    if (call.duration) {
      this.audioDuration.set(call.duration);
    } else if (startTime === 0 && call.platform === 'GoogleMeet' && !this.audioDuration()) {
      // Fetch total duration from backend (only if not in DB and for GoogleMeet)
      this.durationSub = this.callAudio.duration(call).subscribe({
        next: (duration) => {
          if (duration > 0) {
            this.audioDuration.set(duration);
            call.duration = duration;
            // Force-refresh the active call signal
            this.activePlayingCall.update(c => c ? ({ ...c, duration }) : null);
            if (this.activeAuditCall()?.id === call.id) {
              this.activeAuditCall.update(c => c ? ({ ...c, duration }) : null);
            }
          }
        },
        error: (err) => console.error('Error fetching audio duration:', err)
      });
    }

    // Load and play the stream from the requested startTime
    this.streamSub?.unsubscribe();
    this.streamSub = this.callAudio.streamUrl(call, baseOffset).subscribe({
      next: (streamUrl) => {
        this._isChangingSource = true;
        this.audio.pause();
        this.audio.src = streamUrl;
        this.audio.load();

        this.audio.oncanplay = () => {
          this.audio.oncanplay = null;
          this._isChangingSource = false;
          this.audio.play().catch(err => {
            console.error('Error starting audio playback:', err);
            this.isPlaying.set(false);
          });
        };
      },
      error: (err) => {
        console.error('Error loading audio stream:', err);
        this.isPlaying.set(false);
      }
    });
  }

  togglePlay() {
    if (this.isPlaying()) {
      this.audio.pause();
      this.auditTracker.track('AUDIO_PAUSED', { at: this.currentTime() });
    } else {
      // If audio has ended or is at the very end, restart from 0
      const duration = this.audioDuration();
      const current = this.currentTime();
      if (this.audio.ended || (duration > 0 && current >= duration - 0.1)) {
        const call = this.activePlayingCall();
        if (call) {
          this.playCall(call, 0, true);
          return;
        }
      }

      this.auditTracker.track('AUDIO_PLAYED', { at: this.currentTime() });
      this.audio.play().catch(err => {
        console.error('Error resuming audio playback:', err);
        this.isPlaying.set(false);
      });
    }
  }

  seek(event: MouseEvent) {
    const el = event.currentTarget as HTMLElement;
    const rect = el.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, x / rect.width));
    const targetTime = percent * this.audioDuration();

    const call = this.activePlayingCall();
    if (call) {
      this.auditTracker.track('AUDIO_SEEKED', { to: targetTime });
      this.playCall(call, targetTime);
    }
  }

  estimateDuration(item: any): number {
    if (item.duration) return Number(item.duration) || 0;

    let transcriptData = item.transcript;
    if (typeof transcriptData === 'string') {
      try { transcriptData = JSON.parse(transcriptData); } catch { }
    }
    const segmented = this.getSegmentedTranscriptEntries(transcriptData);
    if (segmented?.length) {
      const last = segmented[segmented.length - 1];
      const duration = Math.ceil(last?.end ?? last?.start ?? 0);
      return isFinite(duration) ? duration : 0;
    }

    const transcript = item.googleTranscript || transcriptData?.googleTranscript;
    if (!Array.isArray(transcript) || transcript.length === 0) return 0;

    try {
      const recordingStartTime = item.recordingStartTime;
      const baseTsSec = recordingStartTime
        ? (typeof recordingStartTime === 'number' ? recordingStartTime / 1000 : new Date(recordingStartTime).getTime() / 1000)
        : (transcript[0]?.timestamp || transcript[0]?.time_stamp || 0);

      const lastNode = transcript[transcript.length - 1];
      const lastTs = lastNode?.timestamp || lastNode?.time_stamp || baseTsSec;
      const duration = Math.ceil(Math.max(0, lastTs - baseTsSec));
      return isFinite(duration) ? duration : 0;
    } catch (e) {
      return 0;
    }
  }

  formatTime(seconds: number): string {
    if (!isFinite(seconds) || isNaN(seconds) || seconds === null || seconds === undefined) return '';
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }

  closePlayer() {
    this.audio.pause();
    this.activePlayingCall.set(null);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.activePlayingCall()) return;

    const target = event.target as HTMLElement;

    // Ignore clicks inside the player wrapper
    if (target.closest('.recording-player-card')) return;

    // Ignore clicks on any play button
    if (target.closest('.play-record-btn')) return;

    // Ignore clicks on transcript toggles, sidebar or backdrop
    if (target.closest('.transcript-toggle') || target.closest('.transcript-sidebar') || target.closest('.transcript-backdrop')) return;


    // Close the player
    this.closePlayer();
  }

  openTranscript(call: any) {
    let transcriptObj = call.transcript;
    if (typeof transcriptObj === 'string') {
      try { transcriptObj = JSON.parse(transcriptObj); } catch (e) { }
    }

    const normalizedCall = {
      ...call,
      transcript: transcriptObj,
      formattedTranscript: this.normalizeTranscript({ ...call, transcript: transcriptObj })
    };
    this.activeTranscriptCall.set(normalizedCall);
    // No-ops unless an audit review session is active (i.e. opened from the audit detail).
    this.auditTracker.track('TRANSCRIPT_VIEWED');
  }

  closeTranscript() {
    this.activeTranscriptCall.set(null);
  }

  private mapExcerpts(rawExcerpt: any, agentName: string): any[] {
    if (Array.isArray(rawExcerpt)) {
      return rawExcerpt.map((line: any, i: number) => {
        if (!line?.text) return null;
        const roleRaw = (line.role ?? '').toString().toLowerCase();
        const speaker = (line.speaker ?? '').toString();
        const isAgent = ['advisor', 'agent', 'salesagent'].includes(roleRaw) ||
          (agentName && speaker.toLowerCase().includes(agentName.toLowerCase()));
        const isAI = ['ai', 'llm', 'assistant'].includes(roleRaw);
        const time = line.time || line.formattedTime || '';
        return {
          label: i === 0 ? 'Context' : (isAgent ? 'Current Performance' : 'Context'),
          text: speaker ? `${speaker} said: "${line.text}"` : `"${line.text}"`,
          time,
          role: isAI ? 'AI' : (isAgent ? 'AGENT' : 'CUSTOMER'),
          isWarning: isAgent,
          isSuccess: false
        };
      }).filter(item => item !== null);
    } else if (typeof rawExcerpt === 'string' && rawExcerpt.trim()) {
      const excerptLines = rawExcerpt.split('\n').filter((l: string) => l.trim());
      return excerptLines.map((line: string, i: number) => {
        const match = line.match(/^\[(\d{2}:\d{2})\]\s+([^:]+):\s+(.*)$/);
        if (match) {
          const speaker = match[2];
          const text = match[3];
          const isAgent = ['Advisor', 'Agent', 'SalesAgent', 'salesAgent'].includes(speaker) || (agentName && speaker.toLowerCase().includes(agentName.toLowerCase()));
          const isAI = ['AI', 'llm', 'Assistant'].includes(speaker);

          return {
            label: i === 0 ? 'Context' : (isAgent ? 'Current Performance' : 'Context'),
            text: `${speaker} said: "${text}"`,
            time: match[1],
            role: isAI ? 'AI' : (isAgent ? 'AGENT' : 'CUSTOMER'),
            isWarning: isAgent,
            isSuccess: false
          };
        }
        return { label: 'Excerpt', text: line, time: '', role: 'CUSTOMER', isWarning: false, isSuccess: false };
      });
    }
    return [];
  }

  openAuditDetail(call: any) {
    let reportObj = call.report;
    if (typeof reportObj === 'string') {
      try { reportObj = JSON.parse(reportObj); } catch (e) { }
    }

    let transcriptObj = call.transcript;
    if (typeof transcriptObj === 'string') {
      try { transcriptObj = JSON.parse(transcriptObj); } catch (e) { }
    }

    // Extract call_overview summary
    let overviewSummary = `"The call audit revealed an overall performance score of ${call.rating?.score || 'N/A'}. Detailed segment insights are available below."`;
    if (reportObj?.call_overview?.data && Array.isArray(reportObj.call_overview.data)) {
      const summaryNode = reportObj.call_overview.data.find((d: any) => d.segment === 'summary');
      if (summaryNode && summaryNode.data) {
        overviewSummary = `"${summaryNode.data}"`;
      }
    }

    this.activeAuditCall.set({ ...call, overviewSummary });

    // segment_analysis can be in call.transcript, call.report, or just call.report.data
    const tSA = transcriptObj?.segment_analysis;
    const rSA = reportObj?.segment_analysis;
    const rData = reportObj?.data;

    // Helper to check if we have usable data
    const isPopulated = (obj: any) => {
      if (!obj) return false;
      if (Array.isArray(obj)) return obj.length > 0;
      return Object.keys(obj).length > 0;
    };

    let segmentDataToUse = isPopulated(tSA) ? tSA : (isPopulated(rSA) ? rSA : rData);

    if (segmentDataToUse) {
      let analysisArray: any[] = [];
      if (Array.isArray(segmentDataToUse)) {
        analysisArray = segmentDataToUse;
      } else if (typeof segmentDataToUse === 'object') {
        // If it's a container object with its own 'data' array (like in Screenshot 1)
        if (Array.isArray(segmentDataToUse.data) && (segmentDataToUse.segment === 'segment_analysis' || !segmentDataToUse.segment || !segmentDataToUse.display_name)) {
          analysisArray = segmentDataToUse.data;
        }
        // Or if it's a single segment object (contains core properties)
        else if (segmentDataToUse.segment || segmentDataToUse.display_name || segmentDataToUse.score_proof) {
          analysisArray = [segmentDataToUse];
        } else {
          // It's a map of segments (keys = category names)
          analysisArray = Object.keys(segmentDataToUse).map(key => ({
            ...segmentDataToUse[key],
            _key: key
          }));
        }
      }

      // Filter out non-category segments like overall 'score' or 'weighted_score'
      // BUT keep them if they have a significant display_name (like "Detailed Cell Segments")
      const validCategories = analysisArray.filter(seg =>
        (seg.segment || seg._key) &&
        (seg.segment !== 'weighted_score') &&
        (seg.segment !== 'score' || seg.display_name?.includes('Segment'))
      );

      const categories = validCategories.map((segmentData: any, index: number) => {
        const dataArr = segmentData.data || [];
        const findData = (seg: string) => Array.isArray(dataArr) ? dataArr.find((d: any) => d.segment === seg)?.data : null;
        const nestedData = Array.isArray(dataArr) && dataArr.length === 1 ? dataArr[0] : null;

        // Parse conversation excerpts (Nested in data OR top-level)
        const rawExcerpt = findData('conversation_snippet') || segmentData.conversation_snippet || nestedData?.conversation_snippet || '';
        const agentName = call.agentName || call.agent?.name || '';
        const mappedExcerpts = this.mapExcerpts(rawExcerpt, agentName);

        // Add a recommended approach
        const recommendations = findData('recommendations') || segmentData.recommendations || nestedData?.recommendations;
        if (Array.isArray(recommendations) && recommendations.length > 0) {
          mappedExcerpts.push({
            label: 'Recommended approach',
            role: 'AI',
            text: recommendations[0],
            time: '',
            isWarning: false,
            isSuccess: true
          });
        }

        const title = segmentData.display_name || (segmentData._key ? segmentData._key.replace(/_/g, ' ') : '') || findData('summary_insight') || nestedData?.summary_insight || segmentData.segment?.replace(/_/g, ' ') || 'Category';

        return {
          id: index + 1,
          title: title.charAt(0).toUpperCase() + title.slice(1).toLowerCase(), // Consistent casing
          score: findData('score') ?? segmentData.score ?? nestedData?.score ?? 0,
          justification: findData('score_proof') || segmentData.score_proof || nestedData?.score_proof || '',
          analysis: findData('analysis') || segmentData.analysis || nestedData?.analysis || '',
          analysisSub: findData('summary_insight') || segmentData.summary_insight || nestedData?.summary_insight || '',
          wentWell: Array.isArray(findData('what_went_well')) ? findData('what_went_well') : (Array.isArray(segmentData.what_went_well) ? segmentData.what_went_well : (Array.isArray(nestedData?.what_went_well) ? nestedData.what_went_well : [])),
          opportunities: Array.isArray(findData('areas_for_improvement')) ? findData('areas_for_improvement') : (Array.isArray(segmentData.areas_for_improvement) ? segmentData.areas_for_improvement : (Array.isArray(nestedData?.areas_for_improvement) ? nestedData.areas_for_improvement : (Array.isArray(findData('opportunities')) ? findData('opportunities') : (Array.isArray(segmentData.opportunities) ? segmentData.opportunities : (Array.isArray(nestedData?.opportunities) ? nestedData.opportunities : []))))),
          excerpts: mappedExcerpts
        };
      });
      console.log('[AuditDebug] Extracted auditCategories:', categories);
      this.auditCategories.set(categories);
    } else {
      this.auditCategories.set([]);
    }

    const summaryData = reportObj?.final_summary?.data || [];
    const getSummaryNode = (seg: string) => summaryData.find((d: any) => d.segment === seg)?.data;

    const overviewData = reportObj?.call_overview?.data || [];
    const getOverviewNode = (seg: string) => overviewData.find((d: any) => d.segment === seg)?.data;

    const finalSummary = {
      weightedScore: getSummaryNode('overall_weighted_score') || getSummaryNode('weighted_score') || 0,
      highlights: getSummaryNode('key_highlights') || [],
      performance: getSummaryNode('overall_performance_evaluation') || getSummaryNode('overall_performance') || '',
      coaching: getSummaryNode('top_3_coaching_priorities') || [],
      commentary: getSummaryNode('commentary_on_topic_adherence') || '',
      majorIssues: getSummaryNode('major_issues') || []
    };

    const callOverview = {
      contactType: getOverviewNode('contact_type') || '',
      summary: getOverviewNode('summary') || ''
    };

    console.log('[AuditDebug] Raw reportObj:', reportObj);
    console.log('[AuditDebug] Extracted finalSummary:', finalSummary);
    console.log('[AuditDebug] Extracted callOverview:', callOverview);

    const finalCallData = { ...call, overviewSummary, finalSummary, callOverview };
    this.activeAuditCall.set(finalCallData);
    sessionStorage.setItem('activeAuditCall', JSON.stringify(finalCallData));
    sessionStorage.setItem('auditCategories', JSON.stringify(this.auditCategories())); // store categories too!

    this.showTranscriptionInAudit.set(false);
    this.showAuditDetail.set(true);

    // Join socket room
    this.chatSocketService.joinAuditRoom(finalCallData.id);

    // Fetch the active session from the backend
    this.fetchActiveSession(finalCallData.id);

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  toggleAuditTranscript() {
    const isNowOpen = !this.showTranscriptionInAudit();
    this.showTranscriptionInAudit.set(isNowOpen);
    if (isNowOpen) {
      this.auditTracker.track('TRANSCRIPT_VIEWED');
    }
  }

  closeAuditDetail() {
    if (this.audio) {
      this.audio.pause();
    }

    // Capture data BEFORE nulling signals so the API call is correct.
    const call = this.activeAuditCall();
    const capturedSessionId = this.sessionId;

    // Fire Force/Hard close event if timer is running
    if (this.isTimerRunning() && call) {
      // Mark this session as already closed so ngOnDestroy/onPageHide won't double-close
      this.sessionClosedForAudit = String(call.id);

      // Flag this audit as hard-closed so the timer is NOT auto-resumed
      // if the user reopens the same audit before the backend processes the close.
      sessionStorage.setItem(`hard_closed_audit_${call.id}`, '1');

      this._apiService.post('/call-audit/session/close', {
        auditId: call.id,
        sessionId: capturedSessionId,
        productName: call.productName,
      }).subscribe({
        error: (err) => console.error('[closeAuditDetail] Hard-close failed:', err)
      });

      // Deregister from socket registry so disconnect handler won't also fire close
      this.chatSocketService.deregisterAuditSession(call.id, capturedSessionId);
    }

    if (call) {
      this.chatSocketService.leaveAuditRoom(call.id);
    }

    this.auditTracker.endSession();
    this.stopLocalTimer();
    this.stopOtherAgentTimer();
    this.showAuditDetail.set(false);
    this.showTranscriptionInAudit.set(false);
    this.activeAuditCall.set(null);
    this.activeSession.set(null);

    if (this.fetchSessionSub) {
      this.fetchSessionSub.unsubscribe();
      this.fetchSessionSub = null;
      this.isFetchingSession.set(false);
    }
  }

  markAsVerified() {
    const call = this.activeAuditCall();
    if (!call || call.isVerified) return;

    this.dialog.open(ConfirmationModalComponent, {
      width: '400px',
      data: {
        title: 'Mark as Verified',
        description: 'Are you sure you want to mark this audit summary as verified? This action cannot be undone.',
        confirmLabel: 'Mark as Verified',
        variant: 'primary',
      } as ConfirmationModalData,
    }).afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;

      this._apiService.patch(`/call-audit/${call.id}/verify`, {}).subscribe({
        next: (res: any) => {
          if (this.isTimerRunning()) {
            this._apiService.post('/call-audit/session/close', {
              auditId: call.id,
              sessionId: this.sessionId,
              productName: call.productName,
            }).subscribe();
            this.stopLocalTimer();
            this.activeSession.set(null);
          }
          const updated = res?.data || res;
          this.activeAuditCall.update((c: any) => ({
            ...c,
            isVerified: true,
            verifiedByName: updated?.verifiedByName,
            verifiedAt: updated?.verifiedAt || new Date().toISOString(),
          }));
          this.toasterService.success('Audit summary marked as verified', 'Success');
          // Record the verify event + flush review time, then refresh the activity panel.
          this.auditTracker.trackVerified();
          this.activityPanel?.refresh();
        },
        error: (err) => {
          this.toasterService.error(err?.message || 'Failed to mark as verified', 'Error');
        },
      });
    });
  }

  downloadReport() {
    const call = this.activeAuditCall();
    if (!call) return;
    this.auditTracker.track('REPORT_DOWNLOADED');

    const doc = new jsPDF();
    let y = 20;
    const margin = 20;
    const colWidth = 80;
    const colGap = 10;
    const leftColX = margin;
    const rightColX = margin + colWidth + colGap;

    // Header
    doc.setFontSize(22);
    doc.setTextColor(147, 51, 234); // Purple 600
    doc.setFont("helvetica", "bold");
    doc.text('Call Quality Report', margin, y);
    y += 12;

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 116, 139); // Slate 500
    doc.text(`Business: ${call.business.name} | Agent: ${call.agent.name} | Date: ${call.callType.dateTime}`, margin, y);
    y += 8;

    // --- FINAL SUMMARY SECTION (Moved to top) ---
    const summary = call.finalSummary;
    if (summary) {
      doc.setDrawColor(226, 232, 240);
      doc.line(margin, y, 190, y);
      y += 15;

      doc.setFontSize(16);
      doc.setTextColor(15, 23, 42);
      doc.setFont("helvetica", "bold");
      doc.text('FINAL SUMMARY', margin, y);
      y += 15;

      // Overall Score Badge
      doc.setFillColor(245, 158, 11); // Amber 500
      doc.roundedRect(margin, y, 20, 20, 4, 4, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.text(`${summary.weightedScore}`, margin + 10, y + 10, { align: 'center' });
      doc.setFontSize(8);
      doc.text('SCORE', margin + 10, y + 16, { align: 'center' });

      doc.setTextColor(15, 23, 42);
      doc.setFontSize(14);
      doc.text('Overall Weighted Score', margin + 25, y + 11);
      y += 30;

      // Key Highlights (Full width)
      if (summary.highlights && summary.highlights.length > 0) {
        doc.setFontSize(11);
        doc.setTextColor(34, 197, 94); // Green 500
        doc.text('KEY HIGHLIGHTS', margin, y);
        y += 8;
        doc.setFontSize(9);
        doc.setTextColor(71, 85, 105);
        doc.setFont("helvetica", "normal");
        summary.highlights.forEach((h: string) => {
          const lines = doc.splitTextToSize(`• ${h}`, 170);
          if (y + (lines.length * 5) > 280) { doc.addPage(); y = 20; }
          doc.text(lines, margin, y);
          y += (lines.length * 5) + 3;
        });
        y += 5;
      }

      // Performance Evaluation
      if (summary.performance) {
        doc.setFontSize(11);
        doc.setTextColor(71, 85, 105);
        doc.setFont("helvetica", "bold");
        doc.text('OVERALL PERFORMANCE EVALUATION', margin, y);
        y += 6;

        doc.setFillColor(248, 250, 252);
        const perfLines = doc.splitTextToSize(summary.performance, 160);
        const perfBoxH = (perfLines.length * 5) + 10;
        if (y + perfBoxH > 280) { doc.addPage(); y = 20; }
        doc.roundedRect(margin, y, 170, perfBoxH, 2, 2, 'F');
        doc.setTextColor(15, 23, 42);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.text(perfLines, margin + 5, y + 8);
        y += perfBoxH + 15;
      }

      // Coaching Priorities
      if (summary.coaching && summary.coaching.length > 0) {
        doc.setFontSize(11);
        doc.setTextColor(71, 85, 105);
        doc.setFont("helvetica", "bold");
        doc.text('TOP 3 COACHING PRIORITIES', margin, y);
        y += 8;

        summary.coaching.forEach((c: string) => {
          const plainText = c.replace(/\*\*/g, '');
          doc.setFont("helvetica", "bold");
          const cLines = doc.splitTextToSize(plainText, 160);
          const cBoxH = (cLines.length * 5) + 12;
          if (y + cBoxH > 280) { doc.addPage(); y = 20; }

          doc.setDrawColor(245, 158, 11); // Amber 500 border
          doc.setFillColor(255, 251, 235); // Amber 50
          doc.roundedRect(margin, y, 170, cBoxH, 2, 2, 'FD');

          doc.setTextColor(180, 83, 9); // Amber 800
          doc.setFontSize(9);
          this.renderMarkdownPDF(doc, c, margin + 5, y + 8, 160);
          y += cBoxH + 5;
        });
        y += 10;
      }

      // Add a separator before categories
      doc.setDrawColor(226, 232, 240);
      doc.line(margin, y, 190, y);
      y += 15;
    }

    this.auditCategories().forEach((cat, index) => {
      // Calculate estimated height to check for page break
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      const justificationLines = doc.splitTextToSize(cat.justification, colWidth - 10);

      doc.setFontSize(10);
      doc.setFont("helvetica", "italic");
      const analysisLines = doc.splitTextToSize(`"${cat.analysis}"`, colWidth);
      let analysisSubLines: any[] = [];
      if (cat.analysisSub) {
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        analysisSubLines = doc.splitTextToSize(cat.analysisSub, colWidth);
      }

      doc.setFontSize(9);
      const excerptHeight = (cat.excerpts?.length || 0) * 12; // Approximation

      const wwLinesCount = (cat.wentWell || []).reduce((acc: number, item: string) => acc + doc.splitTextToSize(`• ${item}`, colWidth).length, 0);
      const oppLinesCount = (cat.opportunities || []).reduce((acc: number, item: string) => acc + doc.splitTextToSize(`• ${item}`, colWidth).length, 0);
      const listHeight = Math.max(wwLinesCount, oppLinesCount) * 7 + 15;

      const leftHeight = 20 + (justificationLines.length * 5) + 15 + (analysisLines.length * 5) + (analysisSubLines.length > 0 ? (analysisSubLines.length * 5) + 5 : 0);
      const rightHeight = 10 + excerptHeight;
      const sectionHeight = Math.max(leftHeight, rightHeight) + listHeight + 20;

      if (y + sectionHeight > 280) {
        doc.addPage();
        y = 20;
      }

      // 1. Score Badge & Title
      doc.setFillColor(245, 158, 11); // Amber 500
      doc.roundedRect(leftColX, y, 15, 15, 3, 3, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text(`${cat.score}`, leftColX + 7.5, y + 7, { align: 'center' });
      doc.setFontSize(8);
      doc.text('SCORE', leftColX + 7.5, y + 12, { align: 'center' });

      doc.setTextColor(15, 23, 42); // Slate 900
      doc.setFontSize(14);
      const titleLines = doc.splitTextToSize(cat.title, colWidth - 20);
      doc.text(titleLines, leftColX + 20, y + 6);

      y += 20;
      const columnsTopY = y;

      // --- LEFT COLUMN ---
      doc.setFontSize(9);
      doc.setTextColor(71, 85, 105); // Slate 600
      doc.setFont("helvetica", "bold");
      doc.text('JUSTIFICATION', leftColX, y);
      y += 5;

      // Justification Box
      doc.setFillColor(248, 250, 252); // Slate 50
      const boxPadding = 5;
      const boxH = (justificationLines.length * 5) + (boxPadding * 2);
      doc.roundedRect(leftColX, y, colWidth, boxH, 2, 2, 'F');
      doc.setFont("helvetica", "normal");
      doc.setTextColor(15, 23, 42);
      doc.text(justificationLines, leftColX + boxPadding, y + boxPadding + 3);
      y += boxH + 10;

      // Analysis
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(71, 85, 105);
      doc.text('ANALYSIS & INSIGHT', leftColX, y);
      y += 6;
      doc.setFont("helvetica", "italic");
      doc.setTextColor(15, 23, 42);
      doc.setFontSize(10);
      doc.text(analysisLines, leftColX, y);
      y += (analysisLines.length * 5);

      if (analysisSubLines && analysisSubLines.length > 0) {
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(71, 85, 105);
        y += 2;
        doc.text(analysisSubLines, leftColX, y);
        y += (analysisSubLines.length * 5);
      }

      const leftColBottomY = y;

      // --- RIGHT COLUMN ---
      let ry = columnsTopY;
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(71, 85, 105);
      doc.text('CONVERSATION EXCERPT', rightColX, ry);
      ry += 8;

      if (cat.excerpts && cat.excerpts.length > 0) {
        cat.excerpts.forEach((ex: any) => {
          if (ex.label === 'Recommended approach') {
            ry += 5;
            doc.setFillColor(240, 253, 244); // Green 50
            const plainText = ex.text.replace(/\*\*/g, '');
            doc.setFont("helvetica", "bold");
            const recLines = doc.splitTextToSize(plainText, colWidth - 10);
            const recBoxH = (recLines.length * 5) + 12;
            doc.roundedRect(rightColX, ry, colWidth, recBoxH, 2, 2, 'F');
            doc.setTextColor(21, 128, 61); // Green 700
            doc.setFont("helvetica", "bold");
            doc.text('Recommended approach', rightColX + 5, ry + 6);
            doc.setFontSize(9);
            this.renderMarkdownPDF(doc, ex.text, rightColX + 5, ry + 12, colWidth - 10);
            ry += recBoxH + 5;
          } else {
            doc.setFontSize(8);
            doc.setTextColor(148, 163, 184); // Slate 400
            doc.setFont("helvetica", "bold");
            doc.text(ex.label.toUpperCase(), rightColX, ry);
            ry += 5;

            doc.setFontSize(9);
            doc.setFont("helvetica", "normal");

            // Speaker Logic
            if (ex.role === 'AGENT') {
              doc.setTextColor(79, 70, 229); // Indigo 600
            } else if (ex.role === 'CUSTOMER') {
              doc.setTextColor(37, 99, 235); // Blue 600
            } else if (ex.role === 'AI') {
              doc.setTextColor(147, 51, 234); // Purple 600
            } else {
              doc.setTextColor(71, 85, 105); // Slate 600
            }

            const excerptLines = doc.splitTextToSize(ex.text, colWidth);
            doc.text(excerptLines, rightColX, ry);
            ry += (excerptLines.length * 5) + 6;
          }
        });
      }

      y = Math.max(leftColBottomY, ry) + 10;

      // --- SECOND ROW: WHAT WENT WELL & OPPORTUNITIES ---
      const listStartY = y;

      // What Went Well (Left)
      if (cat.wentWell && cat.wentWell.length > 0) {
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(34, 197, 94); // Green 500
        doc.text('WHAT WENT WELL', leftColX, y);
        y += 6;
        doc.setFont("helvetica", "normal");
        doc.setTextColor(71, 85, 105);
        cat.wentWell.forEach((item: string) => {
          const lines = doc.splitTextToSize(`• ${item}`, colWidth);
          doc.text(lines, leftColX, y);
          y += (lines.length * 5) + 2;
        });
      }
      const wentWellBottomY = y;

      // Opportunities (Right)
      let oy = listStartY;
      if (cat.opportunities && cat.opportunities.length > 0) {
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(245, 158, 11); // Amber 500
        doc.text('OPPORTUNITIES', rightColX, oy);
        oy += 6;
        doc.setFont("helvetica", "normal");
        doc.setTextColor(71, 85, 105);
        cat.opportunities.forEach((item: string) => {
          const lines = doc.splitTextToSize(`• ${item}`, colWidth);
          doc.text(lines, rightColX, oy);
          oy += (lines.length * 5) + 2;
        });
      }

      y = Math.max(wentWellBottomY, oy) + 15;

      // Separator line
      if (index < this.auditCategories().length - 1) {
        doc.setDrawColor(241, 245, 249);
        doc.line(margin, y - 5, 190, y - 5);
      }
    });

    doc.save(`${call.business.name.replace(/\s+/g, '_')}_Call_Report.pdf`);
  }

  downloadTranscript() {
    const call = this.activeAuditCall();
    if (!call || !call.formattedTranscript) return;
    this.auditTracker.track('TRANSCRIPT_DOWNLOADED');

    const doc = new jsPDF();
    let y = 20;
    const margin = 20;

    doc.setFontSize(22);
    doc.setTextColor(147, 51, 234);
    doc.text('Call Transcription', margin, y);
    y += 15;

    doc.setFontSize(12);
    doc.setTextColor(15, 23, 42);
    doc.text(`Business: ${call.business.name}`, margin, y); y += 8;
    doc.text(`Date: ${call.callType.dateTime}`, margin, y); y += 15;

    doc.setFontSize(10);

    call.formattedTranscript.forEach((msg: any) => {
      if (y > 270) { doc.addPage(); y = 20; }

      doc.setFont("helvetica", 'bold');
      if (msg.role === 'AGENT') {
        doc.setTextColor(79, 70, 229); // Indigo 600
      } else if (msg.role === 'CUSTOMER') {
        doc.setTextColor(37, 99, 235); // Blue 600
      } else if (msg.role === 'AI') {
        doc.setTextColor(147, 51, 234); // Purple 600
      } else {
        doc.setTextColor(100, 116, 139); // Slate 500
      }
      doc.text(`[${msg.time}] ${msg.speaker}:`, margin, y); y += 6;

      doc.setFont("helvetica", 'normal');
      doc.setTextColor(15, 23, 42);
      const lines = doc.splitTextToSize(msg.text, 170);

      if (y + (lines.length * 6) > 280) {
        doc.addPage(); y = 20;
      }

      doc.text(lines, margin, y);
      y += (lines.length * 6) + 4;
    });

    doc.save(`${call.business.name.replace(/\s+/g, '_')}_Transcript.pdf`);
  }

  getStars(count: number): number[] {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      if (count >= i) {
        stars.push(1); // full star
      } else if (count >= i - 0.5) {
        stars.push(0.5); // half star
      } else {
        stars.push(0); // empty star
      }
    }
    return stars;
  }

  private renderMarkdownPDF(doc: any, text: string, startX: number, startY: number, maxWidth: number): number {
    let currentX = startX;
    let currentY = startY;
    const lineHeight = 5;

    const paragraphs = text.split(/\r?\n/);

    paragraphs.forEach(p => {
      const tokens = p.split('**');
      tokens.forEach((token, index) => {
        const isBold = index % 2 !== 0;
        doc.setFont("helvetica", isBold ? "bold" : "normal");

        const words = token.split(/(\s+)/); // Keep whitespace
        words.forEach(word => {
          if (!word) return;
          const wordWidth = doc.getTextWidth(word);
          if (word.trim() === '') {
            if (currentX > startX) { currentX += wordWidth; }
            return;
          }
          if (currentX + wordWidth > startX + maxWidth && currentX > startX) {
            currentX = startX;
            currentY += lineHeight;
          }
          doc.text(word, currentX, currentY);
          currentX += wordWidth;
        });
      });
      currentX = startX;
      currentY += lineHeight;
    });

    doc.setFont("helvetica", "normal");
    return currentY;
  }

  // --- Session Tracking Methods ---

  formatElapsed(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const m = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
    const s = (totalSeconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  }

  checkSessionAccess() {
    this._apiService.get('/call-audit/session/access').subscribe({
      next: (res: any) => {
        const hasAccess = res?.data?.hasAccess ?? res?.hasAccess ?? false;
        this.hasSessionAccess.set(hasAccess);
      },
      error: () => this.hasSessionAccess.set(false)
    });
  }

  fetchActiveSession(auditId: string) {
    if (this.fetchSessionSub) {
      this.fetchSessionSub.unsubscribe();
    }

    this.isFetchingSession.set(true);
    this.fetchSessionSub = this._apiService.get(`/call-audit/${auditId}/session/active`).subscribe({
      next: (res: any) => {
        const currentCall = this.activeAuditCall();
        if (!currentCall || String(currentCall.id) !== String(auditId)) {
          return;
        }

        this.isFetchingSession.set(false);
        if (this.isStartingSession() || this.isTimerRunning()) {
          return;
        }

        const session = res?.data || res;
        if (session) {
          // Restore the sessionId so hard-close API calls use the correct ID.
          if (session.sessionId) {
            this.sessionId = session.sessionId;
          }
          // Only check for a same-tab back-button close flag.
          const ssKey = `hard_closed_audit_${auditId}`;
          if (sessionStorage.getItem(ssKey)) {
            sessionStorage.removeItem(ssKey);
            this.activeSession.set(null);
            this.stopOtherAgentTimer();
            return;
          }
          // Show session state (e.g. another user is auditing) but do NOT
          // auto-start the timer — any page unload is treated as a hard close.
          this.activeSession.set(session);

          if (session.userPid !== this.authService.getUserPid()) {
            if (this.hasSessionAccess()) {
              this.startOtherAgentTimer(new Date(session.firstOpenedAt));
            }
          } else {
            this.stopOtherAgentTimer();
          }
        } else {
          this.activeSession.set(null);
          this.stopOtherAgentTimer();
        }
      },
      error: (err) => {
        const currentCall = this.activeAuditCall();
        if (!currentCall || String(currentCall.id) !== String(auditId)) {
          return;
        }
        this.isFetchingSession.set(false);
        console.error('Error fetching active session:', err);
        this.stopOtherAgentTimer();
      }
    });
  }

  setupSocketListeners() {
    this.chatSocketService.connect();
    this.attachAuditSocketListeners();

    // Re-attach listeners on reconnect so they survive network blips
    this.chatSocketService.socket.on('connect', () => {
      this.attachAuditSocketListeners();
    });
  }

  private attachAuditSocketListeners() {
    // Remove any existing listeners first to avoid duplicates
    this.chatSocketService.socket.off('audit.session.start');
    this.chatSocketService.socket.off('audit.session.close');
    this.chatSocketService.socket.off('audit.verified');

    this.chatSocketService.socket.on('audit.session.start', (data: any) => {
      const call = this.activeAuditCall();
      if (call && String(call.id) === String(data.auditId)) {
        this.activeSession.set(data);
        if (data.userPid !== this.authService.getUserPid()) {
          if (this.hasSessionAccess()) {
            this.startOtherAgentTimer(new Date(data.firstOpenedAt));
          }
        } else {
          this.stopOtherAgentTimer();
        }
      }
    });

    this.chatSocketService.socket.on('audit.session.close', (data: any) => {
      const call = this.activeAuditCall();
      if (call && String(call.id) === String(data.auditId)) {
        this.activeSession.set(null);
        this.stopOtherAgentTimer();
      }
    });

    this.chatSocketService.socket.on('audit.verified', (data: any) => {
      const call = this.activeAuditCall();
      if (call && String(call.id) === String(data.auditId)) {
        if (data.userPid !== this.authService.getUserPid()) {
          this.activeAuditCall.update((c: any) => ({
            ...c,
            isVerified: true,
            verifiedByName: data.userName,
            verifiedAt: data.verifiedAt,
          }));
          this.toasterService.info(`Audit summary was marked as verified by ${data.userName || 'another user'}`, 'Notice');
          this.activityPanel?.refresh();
        }
      }
    });
  }

  tearDownSocketListeners() {
    if (this.chatSocketService.socket) {
      this.chatSocketService.socket.off('audit.session.start');
      this.chatSocketService.socket.off('audit.session.close');
      this.chatSocketService.socket.off('audit.verified');
      this.chatSocketService.socket.off('connect', this.attachAuditSocketListeners);
    }
  }

  startOtherAgentTimer(startTime: Date) {
    this.stopOtherAgentTimer();
    this.otherAgentTimerInterval = setInterval(() => {
      const ms = Date.now() - startTime.getTime();
      this.otherAgentElapsedTime.set(this.formatElapsed(ms));
    }, 1000);
  }

  stopOtherAgentTimer() {
    this.otherAgentElapsedTime.set('00:00');
    if (this.otherAgentTimerInterval) {
      clearInterval(this.otherAgentTimerInterval);
    }
  }

  startAuditSession() {
    const call = this.activeAuditCall();
    if (!call || this.isStartingSession()) {
      return;
    }

    // Reset the close guard so a new session can be started
    this.sessionClosedForAudit = null;

    // Begin tracking active QA handling time + user events for this audit.
    this.auditTracker.startSession(call.id, call.botId, call.productName);
    this.sessionId = this.auditTracker.currentSessionId;

    this.isStartingSession.set(true);
    this._apiService.post('/call-audit/session/start', {
      auditId: call.id,
      sessionId: this.sessionId,
      productName: call.productName,
    }).subscribe({
      next: (res: any) => {
        const currentCall = this.activeAuditCall();
        if (!currentCall || String(currentCall.id) !== String(call.id)) {
          this.isStartingSession.set(false);
          const data = res?.data || res;
          if (data && data.sessionId) {
            this._apiService.post('/call-audit/session/close', {
              auditId: call.id,
              sessionId: data.sessionId,
              productName: call.productName,
            }).subscribe({ error: () => void 0 });
          }
          return;
        }

        this.isStartingSession.set(false);
        const data = res?.data || res;
        if (data) {
          this.activeSession.set(data);
          this.stopOtherAgentTimer();
          this.startLocalTimer(new Date(data.firstOpenedAt));

          // Register active audit session in WebSocket, pass firstOpenedAt so
          // the broadcast to other users has the accurate DB timestamp.
          this.chatSocketService.registerAuditSession(
            call.id,
            this.sessionId,
            data.firstOpenedAt
          );
        }
      },
      error: (err) => {
        const currentCall = this.activeAuditCall();
        if (currentCall && String(currentCall.id) === String(call.id)) {
          this.isStartingSession.set(false);
          console.error('Error starting session:', err);
        }
      }
    });
  }

  startLocalTimer(startTime: Date) {
    this.stopLocalTimer();
    this.isTimerRunning.set(true);

    this.timerInterval = setInterval(() => {
      const ms = Date.now() - startTime.getTime();
      this.elapsedTime.set(this.formatElapsed(ms));
    }, 1000);
  }

  stopLocalTimer() {
    this.isTimerRunning.set(false);
    this.elapsedTime.set('00:00');
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  /**
   * Hard close on any page unload (reload, tab close, browser close).
   * The session is closed via a beacon so it fires even if the page tears down mid-request.
   * On reload the audit view is restored from sessionStorage, but the user must
   * click "Start Audit" again — every unload counts as a hard close.
   */
  @HostListener('window:pagehide', ['$event'])
  onPageHide(event: PageTransitionEvent) {
    // persisted = true means the page entered the bfcache (back/forward nav).
    // In that case the Angular component is still alive — nothing to close.
    if (event.persisted) return;

    const call = this.activeAuditCall();
    if (!call || !this.isTimerRunning()) return;

    // Skip if closeAuditDetail already sent the close request for this audit.
    if (this.sessionClosedForAudit === String(call.id)) return;
    this.sessionClosedForAudit = String(call.id);

    const token = sessionStorage.getItem('access_token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    fetch(apiUrlFor('POST', 'call-audit/session/close'), {
      method: 'POST',
      keepalive: true,
      headers,
      body: JSON.stringify({ auditId: call.id, sessionId: this.sessionId }),
      credentials: 'include'
    }).catch(err => console.error('Failed to close session', err));
  }
}
